import React from 'react';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Box, IconButton, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { addMonths, addWeeks, monthRangeLabel, scheduleWeekRangeDateLabel } from '../../sampling/surveyScheduleCalendar';
import type { MonthAnchor } from '../../sampling/surveyScheduleCalendar';

export type SurveyScheduleViewMode = 'weekly' | 'monthly';

type Props = {
  viewMode: SurveyScheduleViewMode;
  onViewModeChange: (mode: SurveyScheduleViewMode) => void;
  weekStart: string;
  onWeekStartChange: (next: string) => void;
  monthAnchor: MonthAnchor;
  onMonthAnchorChange: (next: MonthAnchor) => void;
  weekDates: string[];
  weeklyTestId?: string;
  monthlyTestId?: string;
  hideViewModeToggle?: boolean;
};

export function SurveyScheduleCalendarToolbar({
  viewMode,
  onViewModeChange,
  weekStart,
  onWeekStartChange,
  monthAnchor,
  onMonthAnchorChange,
  weekDates,
  weeklyTestId = 'survey-schedule-view-weekly',
  monthlyTestId = 'survey-schedule-view-monthly',
  hideViewModeToggle = false,
}: Props) {
  const periodTitle =
    viewMode === 'weekly'
      ? `${scheduleWeekRangeDateLabel(weekDates[0] ?? '')} - ${scheduleWeekRangeDateLabel(weekDates[6] ?? '')}`
      : monthRangeLabel(monthAnchor);

  return (
    <Stack spacing={1.5}>
      {!hideViewModeToggle ? (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={viewMode}
            onChange={(_, value: SurveyScheduleViewMode | null) => {
              if (value) onViewModeChange(value);
            }}
            aria-label="Mode tampilan jadwal"
          >
            <ToggleButton value="weekly" aria-label="Tampilan mingguan" data-testid={weeklyTestId}>
              Mingguan
            </ToggleButton>
            <ToggleButton value="monthly" aria-label="Tampilan bulanan" data-testid={monthlyTestId}>
              Bulanan
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      ) : null}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <IconButton
          type="button"
          aria-label={viewMode === 'weekly' ? 'Minggu sebelumnya' : 'Bulan sebelumnya'}
          onClick={() =>
            viewMode === 'weekly'
              ? onWeekStartChange(addWeeks(weekStart, -1))
              : onMonthAnchorChange(addMonths(monthAnchor, -1))
          }
          sx={{ p: 1.25 }}
        >
          <ArrowBackIosNewIcon sx={{ fontSize: 28 }} />
        </IconButton>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, textAlign: 'center', flex: 1 }}>
          {periodTitle}
        </Typography>
        <IconButton
          type="button"
          aria-label={viewMode === 'weekly' ? 'Minggu berikutnya' : 'Bulan berikutnya'}
          onClick={() =>
            viewMode === 'weekly'
              ? onWeekStartChange(addWeeks(weekStart, 1))
              : onMonthAnchorChange(addMonths(monthAnchor, 1))
          }
          sx={{ p: 1.25 }}
        >
          <ArrowForwardIosIcon sx={{ fontSize: 28 }} />
        </IconButton>
      </Box>
    </Stack>
  );
}

export function SurveyScheduleCalendarLegend({
  mode,
  variant = 'planning',
}: {
  mode: SurveyScheduleViewMode;
  variant?: 'planning' | 'overview' | 'readonly' | 'status' | 'status-detail' | 'received' | 'received-detail';
}) {
  const scheduledColor = variant === 'overview' ? 'success.main' : 'primary.main';
  const showReceivedLegend = variant === 'received' || variant === 'received-detail';

  return (
    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
      {mode !== 'monthly' ? (
        <>
          <Box sx={{ width: 18, height: 18, bgcolor: 'grey.300', borderRadius: 0.5, border: '1px solid', borderColor: 'grey.500' }} />
          <Typography variant="caption" color="text.secondary">
            Kosong
          </Typography>
        </>
      ) : null}
      {showReceivedLegend ? (
        <>
          {variant === 'received-detail' ? (
            <>
              <Box sx={{ width: 18, height: 18, bgcolor: 'primary.main', borderRadius: 0.5 }} />
              <Typography variant="caption" color="text.secondary">
                Jadwal / quotation yang dibuka
              </Typography>
            </>
          ) : null}
          <Box sx={{ width: 18, height: 18, bgcolor: 'success.main', borderRadius: 0.5 }} />
          <Typography variant="caption" color="text.secondary">
            Terjadwal
          </Typography>
          <Box sx={{ width: 18, height: 18, bgcolor: 'grey.600', borderRadius: 0.5 }} />
          <Typography variant="caption" color="text.secondary">
            Semua berita acara terbit
          </Typography>
        </>
      ) : variant === 'status' || variant === 'status-detail' ? (
        <>
          <Box sx={{ width: 18, height: 18, bgcolor: 'warning.main', borderRadius: 0.5 }} />
          <Typography variant="caption" color="text.secondary">
            {variant === 'status-detail' ? 'Quotation ini · belum disetujui' : 'Belum disetujui'}
          </Typography>
          <Box sx={{ width: 18, height: 18, bgcolor: 'error.main', borderRadius: 0.5 }} />
          <Typography variant="caption" color="text.secondary">
            {variant === 'status-detail' ? 'Quotation ini · ditolak' : 'Ditolak'}
          </Typography>
          <Box sx={{ width: 18, height: 18, bgcolor: 'success.main', borderRadius: 0.5 }} />
          <Typography variant="caption" color="text.secondary">
            {variant === 'status-detail' ? 'Quotation ini · disetujui' : 'Disetujui'}
          </Typography>
          <Box sx={{ width: 18, height: 18, bgcolor: 'grey.700', borderRadius: 0.5 }} />
          <Typography variant="caption" color="text.secondary">
            {variant === 'status-detail' ? 'Quotation ini · WO dibuat' : 'Work order dibuat'}
          </Typography>
          {variant === 'status-detail' ? (
            <>
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  bgcolor: 'grey.300',
                  borderRadius: 0.5,
                  border: '1px solid',
                  borderColor: 'grey.500',
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Jadwal quotation lain
              </Typography>
            </>
          ) : null}
        </>
      ) : (
        <>
          <Box sx={{ width: 18, height: 18, bgcolor: scheduledColor, borderRadius: 0.5 }} />
          <Typography variant="caption" color="text.secondary">
            Quotation terjadwal
          </Typography>
        </>
      )}
      {variant === 'planning' || variant === 'readonly' ? (
        <>
          <Box
            sx={{
              width: 18,
              height: 18,
              bgcolor: 'grey.300',
              borderRadius: 0.5,
              border: '1px solid',
              borderColor: 'grey.500',
            }}
          />
          <Typography variant="caption" color="text.secondary">
            Jadwal quotation lain
          </Typography>
        </>
      ) : null}
      {mode === 'monthly' && variant === 'planning' ? (
        <Typography variant="caption" color="text.secondary">
          · arahkan ke tanggal lalu klik + untuk tambah slot
        </Typography>
      ) : null}
      {mode === 'monthly' && variant === 'overview' ? (
        <Typography variant="caption" color="text.secondary">
          · klik kartu hijau untuk kelola penjadwalan
        </Typography>
      ) : null}
      {mode === 'monthly' && variant === 'readonly' ? (
        <Typography variant="caption" color="text.secondary">
          · klik kartu status untuk detail jadwal
        </Typography>
      ) : null}
    </Stack>
  );
}
